import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Stack,
  MenuItem,
  useTheme,
  Fade,
} from "@mui/material";
import {
  ArrowBack,
  Send,
  Speed,
  BusinessCenter,
  ConnectWithoutContact,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

export const ExecutivesLanding: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [industry, setIndustry] = useState("");
  const [currentRole, setCurrentRole] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);

  React.useEffect(() => {
    setFadeIn(true);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save to Supabase leads table
      const { error } = await supabase.from("leads").insert({
        email: email.trim(),
        industry: industry,
        lead_type: "enterprise",
        metadata: {
          current_role: currentRole,
        },
      });

      if (error) {
        console.error("Error saving executive lead:", error);
        // Still show success to user, but log the error
        // You might want to show an error message to the user instead
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      // Still show success to user, but log the error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  const industries = [
    "Technology",
    "Healthcare",
    "Finance",
    "Real Estate",
    "Energy",
    "Consumer Goods",
    "Manufacturing",
    "Consulting",
    "Other",
  ];

  const roles = [
    "SVP",
    "VP",
    "Chief of Staff",
    "Senior Director",
    "Director",
    "Senior Manager",
    "Other Executive Role",
  ];

  if (isSubmitted) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Container maxWidth="md">
          <Fade in={true} timeout={500}>
            <Paper
              sx={{
                p: 6,
                textAlign: "center",
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="h3"
                component="h1"
                sx={{ mb: 3, fontWeight: 700 }}
              >
                Thank You!
              </Typography>
              <Typography variant="h6" sx={{ color: "text.secondary", mb: 4 }}>
                We've received your inquiry and our team will be in touch within
                24 hours to discuss how stakeholder mapping can accelerate your
                influence and career growth.
              </Typography>
              <Button
                variant="contained"
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{
                  px: 4,
                  py: 1.5,
                }}
              >
                Back to Homepage
              </Button>
            </Paper>
          </Fade>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderBottom: 1,
          borderColor: "divider",
          backdropFilter: "blur(10px)",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(18, 18, 18, 0.9)"
              : "rgba(255, 255, 255, 0.9)",
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <img
                src="/praytell_logo.png"
                alt="MyCatalyze"
                style={{
                  height: 32,
                  width: "auto",
                  filter:
                    theme.palette.mode === "dark"
                      ? "brightness(0) invert(1)"
                      : "none",
                }}
              />
              <Typography variant="h6" component="h1" fontWeight="bold">
                MyCatalyze
              </Typography>
            </Box>

            <Button
              variant="text"
              onClick={handleBack}
              startIcon={<ArrowBack />}
              sx={{
                color: "text.primary",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              Back to Homepage
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ pt: 15, pb: 8, position: "relative", zIndex: 1 }}>
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Fade in={fadeIn} timeout={1000}>
            <Box sx={{ textAlign: "center", mb: 8 }}>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: {
                    xs: "2.5rem",
                    sm: "3.5rem",
                    md: "4.5rem",
                    lg: "5rem",
                  },
                  fontWeight: 900,
                  mb: 2,
                  background:
                    theme.palette.mode === "dark"
                      ? "linear-gradient(135deg, #ffffff 0%, #b0bec5 100%)"
                      : "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  lineHeight: 1.2,
                }}
              >
                Network to the Next Level
              </Typography>

              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontSize: {
                    xs: "1.5rem",
                    sm: "2rem",
                    md: "2.5rem",
                  },
                  fontWeight: 600,
                  mb: 3,
                  color: "text.secondary",
                  lineHeight: 1.3,
                }}
              >
                Network Stewardship for Executives
              </Typography>

              <Typography
                variant="h6"
                component="p"
                sx={{
                  color: "text.secondary",
                  mb: 4,
                  maxWidth: "800px",
                  mx: "auto",
                  fontSize: { xs: "1rem", md: "1.25rem" },
                  lineHeight: 1.6,
                  fontWeight: 400,
                }}
              >
                We organize, monitor, and strengthen the relationships that
                matter most. MyCatalyze gives you a living map of your network,
                revealing influence patterns, communication gaps, and hidden
                opportunities for deeper connections.
              </Typography>
            </Box>
          </Fade>

          {/* Benefits Section */}
          <Fade in={fadeIn} timeout={1500}>
            <Box sx={{ mb: 8 }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  textAlign: "center",
                  mb: 4,
                  fontWeight: 700,
                }}
              >
                Key Benefits
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${theme.palette.divider}`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#1976d2",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            bgcolor: "#1976d2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <BusinessCenter sx={{ fontSize: 32 }} />
                        </Box>
                        <Typography
                          variant="h5"
                          component="h3"
                          fontWeight={700}
                        >
                          More Deals
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ lineHeight: 1.8 }}
                      >
                        Unlock more opportunities by understanding your complete
                        network. We map internal stakeholders, external
                        partners, and decision-makers so you can identify and
                        pursue more deals with confidence.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${theme.palette.divider}`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#9c27b0",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            bgcolor: "#9c27b0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <Speed sx={{ fontSize: 32 }} />
                        </Box>
                        <Typography
                          variant="h5"
                          component="h3"
                          fontWeight={700}
                        >
                          Close Faster
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ lineHeight: 1.8 }}
                      >
                        Accelerate your deals by leveraging warm introductions
                        and understanding decision-making paths. Discover
                        relationship connections that enable you to close deals
                        faster with the right stakeholders.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      p: 0,
                      border: `1px solid ${theme.palette.divider}`,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(255, 255, 255, 0.8)",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                          : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: "#2e7d32",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            bgcolor: "#2e7d32",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <ConnectWithoutContact sx={{ fontSize: 32 }} />
                        </Box>
                        <Typography
                          variant="h5"
                          component="h3"
                          fontWeight={700}
                        >
                          Stay Connected
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ lineHeight: 1.8 }}
                      >
                        Maintain strong relationships across your network with
                        intelligent stewardship. Track relationship strength,
                        identify connection opportunities, and ensure you never
                        lose touch with key stakeholders who matter most.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Fade>

          {/* Form Section */}
          <Fade in={fadeIn} timeout={2000}>
            <Box>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                  }}
                >
                  Get Started
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    color: "text.secondary",
                    maxWidth: "600px",
                    mx: "auto",
                    fontSize: "1.1rem",
                    lineHeight: 1.7,
                  }}
                >
                  Tell us about your role and we'll show you how stakeholder
                  mapping can accelerate your influence and career growth.
                </Typography>
              </Box>

              <Container maxWidth="sm">
                <Paper
                  sx={{
                    p: 6,
                    bgcolor: "background.paper",
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder="your@email.com"
                      />
                      <TextField
                        fullWidth
                        select
                        label="Industry"
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        required
                      >
                        {industries.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        select
                        label="Current Role"
                        value={currentRole}
                        onChange={(e) => setCurrentRole(e.target.value)}
                        required
                      >
                        {roles.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={isSubmitting}
                        startIcon={<Send />}
                        sx={{
                          px: 6,
                          py: 2,
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          textTransform: "none",
                          boxShadow: 4,
                          "&:hover": {
                            boxShadow: 6,
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.2s ease-in-out",
                        }}
                      >
                        {isSubmitting
                          ? "Submitting..."
                          : "Request Consultation"}
                      </Button>
                    </Stack>
                  </form>
                </Paper>
              </Container>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: "divider",
          py: 4,
          bgcolor: "background.paper",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} MyCatalyze. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
